import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  menuClasses,
} from "react-pro-sidebar";
import { Typography, useTheme } from "@mui/material";
import { Icon } from "../icons";
import { hexToRGBA } from "../../utils/hex-to-rgba";
import { useTranslation } from "react-i18next";

import "./SidebarStyles.css";

const PlanSidebar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const [queryParams, setQueryParams] = useState({});
  const [hasImage, setHasImage] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQueryParams({
      projectName: params.get("projectName"),
      groupName: params.get("groupName"),
      projectId: params.get("pid"),
    });
  }, [location.search]);
  const sidebarMenus = [
    {
      label: "판매계획",
      path: "planning",
      icon: "todoIcon",
      directLink: true,
    },
    {
      label: "생산계획",
      path: "result",
      icon: "trendUpIcon",
      directLink: true,
    },
    {
      label: t("sidebars.plan.ganttChart"),
      path: "gantt",
      icon: "ganttIcon",
      directLink: true,
    },
    {
      label: "재고현황",
      path: "stock",
      icon: "officeIcon",
      directLink: true,
    },
    {
      label: "입출고 현황",
      path: "in_out",
      icon: "addtaskIcon",
      directLink: true,
    },
    // {
    //   label: t("sidebars.plan.graph"),
    //   path: "graph",
    //   icon: "chartIcon",
    //   directLink: true,
    // },
    // {
    //   label: t("sidebars.plan.calendar"),
    //   path: "calendar",
    //   icon: "calendarIcon",
    //   directLink: true,
    // },
    {
      label: t("sidebars.plan.masterData"),
      path: "standard",
      icon: "databaseIcon",
      items: [
        {
          name: t("sidebars.plan.machineManagement"),
          path: "machine",
        },
        {
          name: "자재 관리",
          path: "material",
        },
        {
          name: "BOM 관리",
          path: "bom_management",
        },
        // {
        //   name: "아이템 마스터",
        //   path: "item_m",
        // },
        { name: t("sidebars.plan.itemManagement"), path: "item" },
        { name: t("sidebars.plan.timeManagement"), path: "time" },
        { name: "고급 설정", path: "custom_settings" },
        {
          name: t("sidebars.plan.systemSettings"),
          path: "admin",
        },
        {
          name: "재고 관리(임시)",
          path: "sjh_stock_info",
        },
      ],
    },
  ];
  const handleMenuClick = (path) => {
    const url = `/plan/${group_code}/${project_code}/${path}`;
    navigate(url);
  };
  const handleSubMenuClick = (categoryPath, itemPath) => {
    const url = `/plan/${group_code}/${project_code}/${categoryPath}/${itemPath}`;
    navigate(url);
  };
  const getCurrentCategory = () => {
    const path = location.pathname;
    if (path.includes("/planning")) return "planning";
    if (path.includes("/gantt")) return "gantt";
    if (path.includes("/graph")) return "graph";
    if (path.includes("/calendar")) return "calendar";
    if (path.includes("/standard")) return "standard";
    if (path.includes("/stock")) return "stock";
    if (path.includes("/in_out")) return "in_out";
    if (path.includes("/result")) return "result";
    return null;
  };
  const currentCategory = getCurrentCategory();
  const getCurrentSubMenu = () => {
    const path = location.pathname;
    if (path.includes("/standard/")) {
      const lastSegment = path.split("/").pop();
      return lastSegment;
    }
    return null;
  };
  const currentSubMenu = getCurrentSubMenu();
  const sidebarTheme =
    theme.palette.mode === "light"
      ? {
          sidebar: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
          menu: {
            menuContent: theme.palette.background.default,
            icon: theme.palette.primary.main,
            hover: {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
            disabled: {
              color: theme.palette.action.disabled,
            },
          },
        }
      : {
          sidebar: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          menu: {
            menuContent: theme.palette.background.paper,
            icon: theme.palette.primary.light,
            hover: {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
            disabled: {
              color: theme.palette.action.disabled,
            },
          },
        };
  const menuItemStyles = {
    root: {
      fontSize: "13px",
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      "& svg": {
        width: "20px",
        height: "20px",
      },
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: "#b6b7b9",
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRGBA(
              sidebarTheme.menu.menuContent,
              hasImage && !collapsed ? 0.4 : 1
            )
          : "transparent",
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      "&:hover": {
        backgroundColor: hexToRGBA(
          sidebarTheme.menu.hover.backgroundColor,
          hasImage ? 0.8 : 1
        ),
        color: sidebarTheme.menu.hover.color,
      },
    },
    label: {
      opacity: 0.9,
    },
  };

  return (
    <Sidebar
      collapsed={collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      backgroundColor={hexToRGBA(
        sidebarTheme.sidebar.backgroundColor,
        hasImage ? 0.9 : 1
      )}
      rootStyles={{
        height: "100%",
        color: sidebarTheme.sidebar.color,
        width: "240px",
        minWidth: "240px",
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: "none",
        borderBlock: "none",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <Menu menuItemStyles={menuItemStyles}>
            {sidebarMenus.map((menu) =>
              menu.directLink ? (
                <MenuItem
                  key={menu.path}
                  icon={
                    <Icon
                      name={menu.icon}
                      style={{
                        color:
                          currentCategory === menu.path
                            ? theme.palette.primary.main
                            : "rgba(124, 124, 124, 1)",
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  }
                  onClick={() => handleMenuClick(menu.path)}
                  active={currentCategory === menu.path}
                  style={{
                    color:
                      currentCategory === menu.path
                        ? theme.palette.primary.main
                        : "rgba(124, 124, 124, 1)",
                    fontWeight: 500,
                    fontSize: "15px",
                    paddingBlock: "12px",
                    backgroundColor:
                      currentCategory === menu.path
                        ? theme.palette.primary.lightOpacity
                        : null,
                  }}
                >
                  {menu.label}
                </MenuItem>
              ) : (
                <SubMenu
                  key={menu.path}
                  label={menu.label}
                  icon={
                    <Icon
                      name={menu.icon}
                      style={{
                        color:
                          currentCategory === menu.path
                            ? theme.palette.primary.main
                            : "rgba(124, 124, 124, 1)",
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  }
                  defaultOpen={currentCategory === menu.path}
                  style={{
                    color:
                      currentCategory === menu.path
                        ? theme.palette.primary.main
                        : "rgba(124, 124, 124, 1)",
                    fontWeight: 500,
                    fontSize: "15px",
                  }}
                >
                  {menu.items.map((item) => (
                    <MenuItem
                      key={`${menu.path}-${item.path}`}
                      onClick={() => handleSubMenuClick(menu.path, item.path)}
                      active={
                        currentCategory === menu.path &&
                        currentSubMenu === item.path
                      }
                      style={{
                        height: "auto",
                        borderRadius: "6px",
                        marginBlock: "2px",
                        marginInline: "10px",
                        paddingBlock: "8px",
                        paddingInline: "40px",
                        backgroundColor:
                          location.pathname ===
                          `/plan/${group_code}/${project_code}/${menu.path}/${item.path}`
                            ? theme.palette.primary.lightOpacity
                            : null,
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          opacity: 0.9,
                          color:
                            currentCategory === menu.path &&
                            currentSubMenu === item.path
                              ? theme.palette.primary.main
                              : "rgba(124, 124, 124, 1)",
                        }}
                      >
                        {item.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </SubMenu>
              )
            )}
          </Menu>
        </div>
      </div>
    </Sidebar>
  );
};

export default PlanSidebar;
